import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type {
  ContradictionFieldRef,
  ContradictionFinding,
} from '../../../services/api/types.gen';
import { resolveCategoryLabel } from '../OASISFormLabels';
import { ContradictionRuleTypeIcon } from './ContradictionRuleTypeIcon';
import {
  contradictionLocationIsOnCategory,
  primaryCategoryForContradictionLocation,
  suggestionCodesFromAction,
} from './contradictionNavigation';
import {
  formatCodeWithDescription,
  lookupIcdDescriptions,
} from './lookupIcdDescriptions';

// The chip stays visually small so the row reads tight, and `before:-inset-y-1`
// grows the touch target to 28px without taking any layout space — it is the
// primary finding→field affordance on a ~390px phone.
const locationChipClassName =
  "relative inline-flex items-center gap-0.5 rounded-sm px-1.5 py-0.5 text-xs leading-4 text-foreground/70 transition-colors before:absolute before:inset-x-0 before:-inset-y-1 before:content-[''] hover:text-foreground hover:underline";

export interface ContradictionFindingCardProps {
  finding: ContradictionFinding;
  currentCategory: string | null;
  qidToCategoryMap: Record<string, string>;
  labelByCategory: Record<string, string>;
  /** Card body: prefer highlighting locations on the open section. */
  onJump: (finding: ContradictionFinding) => void;
  /**
   * Section chip: highlight that location, switching section first when it
   * lives elsewhere (same scroll/highlight path as a CMS jump).
   */
  onHighlightLocation: (location: ContradictionFieldRef) => void;
}

export function ContradictionFindingCard({
  finding,
  currentCategory,
  qidToCategoryMap,
  labelByCategory,
  onJump,
  onHighlightLocation,
}: ContradictionFindingCardProps) {
  const [suggestionLines, setSuggestionLines] = useState<string[]>([]);
  // Resolve codes each render; effect keys on the stable joined string so a
  // fresh remediation.action object from revalidation does not re-fetch.
  const suggestionCodes = suggestionCodesFromAction(finding.remediation.action);
  const suggestionCodesKey = suggestionCodes.join('|');

  useEffect(() => {
    let cancelled = false;
    const codes = suggestionCodesKey
      ? suggestionCodesKey.split('|')
      : ([] as string[]);
    if (codes.length === 0) {
      setSuggestionLines([]);
      return;
    }
    void lookupIcdDescriptions(codes).then(descriptions => {
      if (cancelled) return;
      setSuggestionLines(
        codes.map(code => formatCodeWithDescription(code, descriptions))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [suggestionCodesKey]);

  const locations = finding.error_locations || [];

  return (
    <div className='rounded-md border border-border/80 bg-background/80 text-xs transition-shadow duration-300 ease-out hover:border-foreground/30'>
      <button
        type='button'
        className='group w-full cursor-pointer px-2.5 py-1.5 text-left'
        onClick={() => onJump(finding)}
        aria-label={`Contradiction: ${finding.remediation.guidance}`}
      >
        <div className='flex items-start gap-2'>
          <div className='mt-0.5 flex shrink-0 items-center gap-0.5 text-muted-foreground'>
            {finding.types.map(type => (
              <ContradictionRuleTypeIcon key={type} type={type} />
            ))}
          </div>
          <div className='min-w-0 flex-1'>
            <p className='font-medium leading-snug text-foreground/90'>
              {finding.remediation.guidance}
            </p>
            {suggestionLines.length > 0 && (
              <p className='mt-0.5 leading-snug text-muted-foreground'>
                {finding.remediation.kind === 'suggest_diagnosis_choice'
                  ? 'Suggested diagnoses: '
                  : 'Suggested diagnosis: '}
                <span className='text-foreground/80'>
                  {suggestionLines.join('; ')}
                </span>
              </p>
            )}
          </div>
          <span className='shrink-0 select-none font-mono text-[10px] text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100'>
            {finding.rule_id}
          </span>
        </div>
      </button>

      {/* `gap-y-1` keeps the chips' invisible touch areas from overlapping when
          the row wraps on a narrow screen. */}
      {locations.length > 0 && (
        <div className='flex flex-wrap items-center gap-x-1.5 gap-y-1 border-t border-border/60 px-2.5 py-0.5'>
          {locations.map((location, index) => {
            const onCurrent = contradictionLocationIsOnCategory(
              location,
              currentCategory,
              qidToCategoryMap
            );
            const sectionId = primaryCategoryForContradictionLocation(
              location,
              qidToCategoryMap
            );
            const sectionLabel = sectionId
              ? resolveCategoryLabel(sectionId, labelByCategory)
              : null;

            return (
              <span
                key={`${location.source}-${location.oasis_item_id ?? location.field ?? location.label}-${index}`}
                className='inline-flex items-center gap-0.5'
              >
                {index > 0 && (
                  <span
                    className='text-[10px] leading-4 text-muted-foreground/40'
                    aria-hidden
                  >
                    ·
                  </span>
                )}
                {sectionLabel ? (
                  <button
                    type='button'
                    className={locationChipClassName}
                    onClick={() => onHighlightLocation(location)}
                    aria-label={
                      onCurrent
                        ? `Highlight ${location.label} on ${sectionLabel}`
                        : `Go to ${sectionLabel} and highlight ${location.label}`
                    }
                  >
                    {!onCurrent && (
                      <ArrowRight className='h-2.5 w-2.5 shrink-0 opacity-70' />
                    )}
                    {sectionLabel}
                  </button>
                ) : null}
                <span className='text-[10px] leading-4 text-muted-foreground/70'>
                  {location.label}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
